import React, { useEffect, useState, memo } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { getContentLoadStatus } from '../../../../content';
/**
 * LoadedModulesWidget — Boot Flow Refactor diagnostics.
 *
 * Shows which of the 10 content modules are currently loaded into memory
 * and which are still pending. Polls every second so admins can watch
 * idle prefetch progress in real-time.
 */
export function LoadedModulesWidget() {
  const [status, setStatus] = useState(() => getContentLoadStatus());
  // F-A2 (19 May 2026): poll only while the tab is visible. When the admin
  // tabs away (or locks their screen) we stop polling and resume on focus —
  // no point hammering `getContentLoadStatus()` every second on a hidden
  // tab. Audit §13 (perf hygiene).
  useEffect(() => {
    if (status.allLoaded) return;
    let intervalId: number | null = null;
    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => {
        const next = getContentLoadStatus();
        setStatus(next);
        if (next.allLoaded && intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    };
    const stopPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Catch up immediately on resume so the UI isn't stale for up to 1s.
        const next = getContentLoadStatus();
        setStatus(next);
        if (!next.allLoaded) startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [status.allLoaded]);
  const {
    totalModules,
    loadedModuleCodes,
    pendingModuleCodes,
    allLoaded
  } = status;
  const loadedCount = loadedModuleCodes.length;
  const pct = Math.round(loadedCount / totalModules * 100);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7829" data-mp="9k2t1yc6ys9w">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-7830" data-mp="ef08bn02dxv8">
        <div className="flex items-center gap-2" data-id="element-7831" data-mp="igboz30yn8ra">
          <div className="w-4 h-4 text-gray-500" data-id="element-7832" data-mp="3hunqk2tmeol8" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-7833" data-mp="3mspbyueiqzzv">
            Geladen modules
          </h3>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${allLoaded ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-7834" data-mp="3qtsrffsnmu0k">
          {loadedCount} / {totalModules}
        </span>
      </div>
      <div className="p-4 sm:p-5 space-y-4" data-id="element-7835" data-mp="163junm2awjtu">
        <div data-id="element-7836" data-mp="1b366ps6e7u12">
          <div className="flex justify-between text-xs mb-1.5" data-id="element-7837" data-mp="1b2n037m70s2c">
            <span className="text-gray-700 font-medium" data-id="element-7838" data-mp="1b594iydsergc">Voortgang</span>
            <span className="font-semibold text-gray-600" data-id="element-7839" data-mp="1l6lbih2uwon5">{pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-7840" data-mp="1g91ooss3f3jl">
            <div className={`h-2 rounded-full transition-all ${allLoaded ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{
            width: `${pct}%`
          }} data-id="element-7841" data-mp="1bh1t1cj22wst" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-7842" data-mp="12i6kvuwgbcmv">
          {loadedModuleCodes.map(code => <div key={code} className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-800" data-id="element-7843" data-mp="xgqvxrr5klz7">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-7844" data-mp="ohm1awippcsb" />
              <span className="font-mono font-medium" data-id="element-7845" data-mp="oj6sqd9qcfly">{code}</span>
            </div>)}
          {pendingModuleCodes.map(code => <div key={code} className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-500" data-id="element-7846" data-mp="2ivws3aivah0q">
              <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" data-id="element-7847" data-mp="2uwwdykjy3d5o" />
              <span className="font-mono font-medium" data-id="element-7848" data-mp="2uv217titurco">{code}</span>
            </div>)}
        </div>

        {!allLoaded && <p className="text-xs text-gray-500" data-id="element-7849" data-mp="2kyfibaa2aada">
            Idle prefetch is bezig op de achtergrond. Modules worden in-place
            aan de indexen toegevoegd zodra ze geladen zijn.
          </p>}
      </div>
    </div>;
}